import * as amplitude from "@amplitude/analytics-browser";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useHtmlEventListener } from "@redotech/react-util/event";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import LayoutLeft from "@redotech/redo-icons/layout-left.svg";
import LayoutLeftFilled from "@redotech/redo-icons/layout-left_filled.svg";
import PhoneIcon from "@redotech/redo-icons/phone.svg";
import PhoneFilledIcon from "@redotech/redo-icons/phone_filled.svg";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import GearIcon from "@redotech/redo-icons/settings-02.svg";
import GearFilledIcon from "@redotech/redo-icons/settings-02_filled.svg";
import classNames from "classnames";
import {
  createContext,
  JSXElementConstructor,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { Flex } from "../../../flex";
import { Text } from "../../../text";
import * as darkModeCss from "../../../theme/component-colors-dark.module.css";
import { Tooltip } from "../../../tooltip/tooltip";
import { usePhoneWidget } from "../../../voice/shared/phone-widget-context";
import { RedoIcon } from "../../icon/redo-icon";
import type { RedoListItem } from "../../list/redo-list";
import { RedoListDropdown } from "../../list/redo-list-dropdown";
import { RedoNavigationTeamItem } from "../common/redo-nav-team-item";
import { UserProfile } from "../common/redo-nav-user-profile";
import * as navigationCss from "../redo-navigation.module.css";
import {
  PrimaryNavOverflowPopover,
  tabToListItem,
  usePrimaryNavOverflow,
} from "./redo-primary-nav-rail";
import { PrimaryNavigationTab } from "./redo-primary-nav-tab";

interface BasePrimaryTab {
  id: string;
  hasNotification: boolean;
  UnfilledIcon: JSXElementConstructor<any>;
  FilledIcon: JSXElementConstructor<any>;
  friendlyName: string;
  hideLabel?: boolean;
}

export interface LinkPrimaryTab extends BasePrimaryTab {
  type: "link";
  href: string;
  pathRoot: string;
}

interface ButtonPrimaryTab extends BasePrimaryTab {
  type: "button";
  onClick: () => void;
}

export type PrimaryTab = LinkPrimaryTab | ButtonPrimaryTab;

interface PrimaryTabSections {
  core: LinkPrimaryTab[];
  activeProducts: LinkPrimaryTab[];
  inactiveProducts: LinkPrimaryTab[];
}

interface RedoPrimaryNavProps {
  toggleCollapseMode: () => void;
  isCollapseMode: boolean;
  enterSettingsUrl: string | null;
  settingsRoot: string;
  primaryTabSections: PrimaryTabSections;
  selectedTabId: string | undefined;
  hideTeamSwitcher?: boolean;
}

/**
 * Applies a stored order to a list of tabs. Tabs present in storedOrder
 * come first (in that order), followed by any new tabs not in the stored order.
 */
function applyStoredOrder(
  tabs: LinkPrimaryTab[],
  storedOrder: string[],
): LinkPrimaryTab[] {
  if (storedOrder.length === 0) {
    return tabs;
  }
  const tabMap = new Map(tabs.map((tab) => [tab.id, tab]));
  const ordered: LinkPrimaryTab[] = [];
  for (const id of storedOrder) {
    const tab = tabMap.get(id);
    if (tab) {
      ordered.push(tab);
      tabMap.delete(id);
    }
  }
  // Append any tabs not in stored order (new products)
  for (const tab of tabMap.values()) {
    ordered.push(tab);
  }
  return ordered;
}

const PrimaryNavAddPopover = memo(function PrimaryNavAddPopover({
  availableTabs,
  selectedTabId,
}: {
  availableTabs: LinkPrimaryTab[];
  selectedTabId: string | undefined;
}) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const iconOnly = useContext(PrimaryNavIconOnlyContext);
  const navigate = useNavigate();

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const listItems: RedoListItem<LinkPrimaryTab>[] = useMemo(
    () => availableTabs.map(tabToListItem),
    [availableTabs],
  );

  const handleItemSelected = useCallback(
    ({ item }: { item: RedoListItem<LinkPrimaryTab> }) => {
      const tab = item.value;
      amplitude.track("nav/sidebar-item-clicked", {
        section: tab.id,
        item: tab.id,
      });
      navigate(tab.href);
    },
    [navigate],
  );

  const addButton = (
    <Flex
      align="center"
      as="button"
      className={navigationCss.primaryTab}
      data-testid="primary-nav-add"
      dir="column"
      gap="xxs"
      justify="center"
      onClick={handleOpen}
      ref={setAnchorEl}
    >
      <Flex
        align="center"
        className={navigationCss.primaryTabIconBox}
        justify="center"
      >
        <RedoIcon color="tertiary" Icon={PlusIcon} size={20} />
      </Flex>
      {!iconOnly && (
        <Text
          as="span"
          fontSize="xxs"
          textColor="secondary"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {t("Add", { context: "navigation menu" })}
        </Text>
      )}
    </Flex>
  );

  return (
    <>
      {iconOnly ? (
        <Tooltip
          darkMode
          placement="right"
          title={t("Add", { context: "navigation menu" })}
        >
          {addButton}
        </Tooltip>
      ) : (
        addButton
      )}
      <RedoListDropdown
        darkMode
        dropdownAnchor={anchorEl}
        dropdownOpen={open}
        dropdownPlacement="right-start"
        items={listItems}
        itemSelected={handleItemSelected}
        selectedItems={selectedTabId}
        selectionVariant="none"
        setDropdownOpen={setOpen}
        size="xs"
      />
    </>
  );
});

const SortablePrimaryTab = memo(function SortablePrimaryTab({
  tab,
  lastDragTimeRef,
  selectedTabId,
}: {
  tab: LinkPrimaryTab;
  lastDragTimeRef: React.MutableRefObject<number>;
  selectedTabId: string | undefined;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tab.id });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
    // Block pointer events on the inner link while dragging
    pointerEvents: isDragging ? "none" : undefined,
  };

  const handleClickCapture = useCallback(
    (e: React.MouseEvent) => {
      // Block clicks for 500ms after any drag ends
      if (Date.now() - lastDragTimeRef.current < 500) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    [lastDragTimeRef],
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClickCapture={handleClickCapture}
    >
      <PrimaryNavigationTab
        sectionType="active"
        selectedTabId={selectedTabId}
        tab={tab}
      />
    </div>
  );
});

const ACTIVE_NAV_ORDER_KEY = "redo_nav_order_active";

function readStoredOrder(): string[] {
  try {
    const raw = localStorage.getItem(ACTIVE_NAV_ORDER_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

const SortableActiveSection = memo(function SortableActiveSection({
  tabs,
  selectedTabId,
  onReorder,
  overflowedActiveTabIds = [],
}: {
  tabs: LinkPrimaryTab[];
  selectedTabId: string | undefined;
  onReorder: (newOrder: string[]) => void;
  overflowedActiveTabIds?: string[];
}) {
  const lastDragTimeRef = useRef(0);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      lastDragTimeRef.current = Date.now();

      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }
      const oldIndex = tabs.findIndex((t) => t.id === active.id);
      const newIndex = tabs.findIndex((t) => t.id === over.id);
      amplitude.track("nav/tab-reordered", {
        tab: active.id,
        fromIndex: oldIndex,
        toIndex: newIndex,
      });
      const reordered = arrayMove(tabs, oldIndex, newIndex);
      const newOrder = [
        ...reordered.map((t) => t.id),
        ...overflowedActiveTabIds,
      ];
      onReorder(newOrder);
    },
    [tabs, onReorder, overflowedActiveTabIds],
  );

  const tabIds = useMemo(() => tabs.map((t) => t.id), [tabs]);

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      <SortableContext items={tabIds} strategy={verticalListSortingStrategy}>
        <Flex dir="column" gap="xl">
          {tabs.map((tab) => (
            <SortablePrimaryTab
              key={tab.id}
              lastDragTimeRef={lastDragTimeRef}
              selectedTabId={selectedTabId}
              tab={tab}
            />
          ))}
        </Flex>
      </SortableContext>
    </DndContext>
  );
});

const PRIMARY_NAV_WIDTH_KEY = "redo_primary_nav_width";
const DEFAULT_PRIMARY_NAV_WIDTH = 72;
const MIN_PRIMARY_NAV_WIDTH = 48;
const ICON_ONLY_THRESHOLD = 64;

function readStoredWidth(): number {
  try {
    const raw = localStorage.getItem(PRIMARY_NAV_WIDTH_KEY);
    if (raw) {
      const parsed = Number(raw);
      if (
        !Number.isNaN(parsed) &&
        parsed >= MIN_PRIMARY_NAV_WIDTH &&
        parsed <= DEFAULT_PRIMARY_NAV_WIDTH
      ) {
        return parsed;
      }
    }
  } catch {
    // ignore
  }
  return DEFAULT_PRIMARY_NAV_WIDTH;
}

export const PrimaryNavIconOnlyContext = createContext(false);

export const RedoPrimaryNav = memo(function RedoPrimaryNav({
  toggleCollapseMode,
  isCollapseMode,
  enterSettingsUrl,
  settingsRoot,
  primaryTabSections,
  selectedTabId,
  hideTeamSwitcher = false,
}: RedoPrimaryNavProps) {
  return (
    <NewPrimaryNavLayout
      enterSettingsUrl={enterSettingsUrl}
      hideTeamSwitcher={hideTeamSwitcher}
      isCollapseMode={isCollapseMode}
      primaryTabSections={primaryTabSections}
      selectedTabId={selectedTabId}
      settingsRoot={settingsRoot}
      toggleCollapseMode={toggleCollapseMode}
    />
  );
});

const NewPrimaryNavLayout = memo(function NewPrimaryNavLayout({
  toggleCollapseMode,
  isCollapseMode,
  enterSettingsUrl,
  settingsRoot,
  primaryTabSections,
  selectedTabId,
  hideTeamSwitcher = false,
}: RedoPrimaryNavProps) {
  const { isVisible, toggleVisibility, isVoiceAvailable } = usePhoneWidget();

  const [navWidth, setNavWidth] = useState(readStoredWidth);
  const [isResizing, setIsResizing] = useState(false);
  const isDragging = useRef(false);
  const lastX = useRef(0);

  const iconOnly = navWidth < ICON_ONLY_THRESHOLD;

  const [storedOrder, setStoredOrder] = useState<string[]>(readStoredOrder);

  const handleReorder = useCallback((newOrder: string[]) => {
    setStoredOrder(newOrder);
    localStorage.setItem(ACTIVE_NAV_ORDER_KEY, JSON.stringify(newOrder));
  }, []);

  const orderedActiveProducts = useMemo(
    () => applyStoredOrder(primaryTabSections.activeProducts, storedOrder),
    [primaryTabSections.activeProducts, storedOrder],
  );

  // The Add button is a virtual trailing slot whose dropdown contains the
  // inactive products. It counts as one item for overflow math but is
  // rendered separately (and folded into More if it overflows).
  const flatTabs = useMemo(
    () => [...primaryTabSections.core, ...orderedActiveProducts],
    [primaryTabSections.core, orderedActiveProducts],
  );

  const coreCount = primaryTabSections.core.length;
  const activeCount = primaryTabSections.activeProducts.length;
  const hasInactive = primaryTabSections.inactiveProducts.length > 0;
  const totalSlots = flatTabs.length + (hasInactive ? 1 : 0);

  // Section boundaries: indices where dividers appear between sections.
  // A divider renders before active products (at coreCount) when there are
  // any active products. The Add button replaces the prior inactive-products
  // divider, so no boundary is added for it.
  const sectionBoundaries = useMemo(() => {
    const boundaries: number[] = [];
    if (activeCount > 0) {
      boundaries.push(coreCount);
    }
    return boundaries;
  }, [coreCount, activeCount]);

  const { visibleCount, isOverflowing, containerRef, tabRef } =
    usePrimaryNavOverflow(totalSlots, sectionBoundaries);

  // Visible counts split across the real tabs and the trailing Add slot.
  const visibleRealCount = Math.min(visibleCount, flatTabs.length);
  const showAddButton = hasInactive && visibleCount > flatTabs.length;
  const overflowTabs = flatTabs.slice(visibleRealCount);

  const hasVisibleActive = visibleRealCount > coreCount && activeCount > 0;

  const visibleCoreTabs = flatTabs.slice(
    0,
    Math.min(visibleRealCount, coreCount),
  );
  const visibleActiveTabs = flatTabs.slice(coreCount, visibleRealCount);
  const overflowedActiveTabIds = useMemo(
    () =>
      orderedActiveProducts.slice(visibleActiveTabs.length).map((t) => t.id),
    [orderedActiveProducts, visibleActiveTabs.length],
  );

  const handleMouseDown = useHandler((e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    lastX.current = e.clientX;
    setIsResizing(true);
  });

  useHtmlEventListener(document.documentElement, "mousemove", (e) => {
    if (!isDragging.current) {
      return;
    }
    const delta = e.clientX - lastX.current;
    lastX.current = e.clientX;
    setNavWidth((prev) =>
      Math.max(
        MIN_PRIMARY_NAV_WIDTH,
        Math.min(DEFAULT_PRIMARY_NAV_WIDTH, prev + delta),
      ),
    );
  });

  useHtmlEventListener(document.documentElement, "mouseup", () => {
    if (!isDragging.current) {
      return;
    }
    isDragging.current = false;
    setIsResizing(false);
    setNavWidth((w) => {
      localStorage.setItem(PRIMARY_NAV_WIDTH_KEY, String(w));
      return w;
    });
  });

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--redo-primary-nav-width",
      `${navWidth}px`,
    );
    return () => {
      document.documentElement.style.removeProperty("--redo-primary-nav-width");
    };
  }, [navWidth]);

  return (
    <PrimaryNavIconOnlyContext.Provider value={iconOnly}>
      <Flex
        align="center"
        bgColor="tertiary"
        className={classNames(
          darkModeCss.darkMode,
          navigationCss.primaryNav,
          isResizing && navigationCss.primaryNavResizing,
        )}
        data-arbiter="RedoPrimaryNav"
        dir="column"
        grow={1}
        justify="space-between"
        px="xs"
        py="xl"
        style={{ width: navWidth, maxWidth: navWidth }}
      >
        <Flex
          align="center"
          className={navigationCss.primaryNavTabSection}
          dir="column"
          gap="xl"
          grow={1}
          shrink={1}
        >
          {!hideTeamSwitcher && <RedoNavigationTeamItem />}
          <Flex
            className={navigationCss.primaryNavTabSection}
            dir="column"
            gap="xl"
            grow={1}
            ref={containerRef}
            shrink={1}
          >
            {visibleCoreTabs.map((tab, i) =>
              i === 0 ? (
                <div key={tab.id} ref={tabRef}>
                  <PrimaryNavigationTab
                    selectedTabId={selectedTabId}
                    tab={tab}
                  />
                </div>
              ) : (
                <PrimaryNavigationTab
                  key={tab.id}
                  sectionType="core"
                  selectedTabId={selectedTabId}
                  tab={tab}
                />
              ),
            )}
            {hasVisibleActive && (
              <>
                <Flex className={navigationCss.primaryNavDivider} />
                <SortableActiveSection
                  onReorder={handleReorder}
                  overflowedActiveTabIds={overflowedActiveTabIds}
                  selectedTabId={selectedTabId}
                  tabs={visibleActiveTabs}
                />
              </>
            )}
            {showAddButton && (
              <PrimaryNavAddPopover
                availableTabs={primaryTabSections.inactiveProducts}
                selectedTabId={selectedTabId}
              />
            )}
            {isOverflowing && (
              <PrimaryNavOverflowPopover
                addSectionTabs={
                  hasInactive && !showAddButton
                    ? primaryTabSections.inactiveProducts
                    : []
                }
                overflowTabs={overflowTabs}
                selectedTabId={selectedTabId}
              />
            )}
          </Flex>
        </Flex>
        <Flex align="center" dir="column" gap="xl" shrink={0}>
          <PrimaryNavigationTab
            manuallySelected={isCollapseMode}
            tab={{
              id: "collapse",
              UnfilledIcon: LayoutLeft,
              FilledIcon: LayoutLeftFilled,
              type: "button",
              friendlyName: "Collapse",
              hasNotification: false,
              hideLabel: true,
              onClick: toggleCollapseMode,
            }}
          />
          {isVoiceAvailable && (
            <PrimaryNavigationTab
              manuallySelected={isVisible}
              tab={{
                id: "phone",
                UnfilledIcon: PhoneIcon,
                FilledIcon: PhoneFilledIcon,
                type: "button",
                friendlyName: "Phone",
                hasNotification: false,
                hideLabel: true,
                onClick: toggleVisibility,
              }}
            />
          )}
          {enterSettingsUrl && (
            <PrimaryNavigationTab
              tab={{
                id: "settings",
                hasNotification: false,
                UnfilledIcon: GearIcon,
                FilledIcon: GearFilledIcon,
                type: "link",
                href: enterSettingsUrl,
                hideLabel: true,
                friendlyName: "Settings",
                pathRoot: settingsRoot,
              }}
            />
          )}
          <UserProfile collapsed />
        </Flex>
        <div
          className={navigationCss.primaryNavResizeHandle}
          data-resizing={isResizing || undefined}
          onMouseDown={handleMouseDown}
        />
      </Flex>
    </PrimaryNavIconOnlyContext.Provider>
  );
});
